import { NavContext } from '@ionic/react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import { useAccount, useAuth, useDentist } from '@/contexts';
import { getClinic, getClinicByMobileNumber } from '@/data/Clinics';
import { getDentistsByMobileNumber, updateDentist } from '@/data/Dentists';
import { useToast } from '@/hooks';
import {
  OTP_ERRORS,
  OTP_LOADING_DELAY_IN_MS,
  SIGN_IN_STEPS
} from '@/utils/constants';
import { ROUTES } from '@/utils/routes';
import { supabase } from '@/utils/supabase';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface SignInContextProps {
  loading: boolean;
  currentStep: string;
  setCurrentStep: React.Dispatch<React.SetStateAction<string>>;
  goBack: () => void;
  mobileNumber: string | null;
  setMobileNumber: React.Dispatch<React.SetStateAction<string | null>>;
  sendOtp: () => Promise<void>;
  resendOtp: () => Promise<void>;
  verifyOtp: (otp: string) => Promise<void>;
  isResendDisabled: boolean;
  setIsResendDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}

const SignInContext = createContext<SignInContextProps | undefined>(undefined);

interface SignInProviderProps {
  children: React.ReactNode;
  onAfterSignIn?: () => void;
}

export const SignInProvider: React.FC<SignInProviderProps> = ({
  children,
  onAfterSignIn
}) => {
  const { navigate, goBack } = useContext(NavContext);
  const { setShouldSaveAccount } = useAccount();
  const { authenticated, user, signIn } = useAuth();
  const { selectDentist } = useDentist();
  const displayToast = useToast();
  const queryClient = useQueryClient();

  const [loadingVerification, setLoadingVerification] =
    useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<string>(
    SIGN_IN_STEPS.MOBILE_NUMBER
  );
  const [mobileNumber, setMobileNumber] = useState<string | null>(null);
  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(true);

  const onDone = useCallback(async () => {
    setShouldSaveAccount(true);
    selectDentist(null);
    navigate(ROUTES.SELECT_DENTIST);
  }, [navigate, selectDentist, setShouldSaveAccount]);

  const sendOtpMutation = useMutation({
    mutationFn: async () => {
      if (!mobileNumber) throw new Error(OTP_ERRORS.MOBILE_NUMBER_IS_REQUIRED);

      const clinic = await queryClient.fetchQuery({
        queryKey: ['clinicByMobileNumber', mobileNumber],
        queryFn: () => getClinicByMobileNumber(mobileNumber)
      });
      const dentists = await queryClient.fetchQuery({
        queryKey: ['dentistsByMobileNumber', mobileNumber],
        queryFn: () => getDentistsByMobileNumber(mobileNumber)
      });

      if (!clinic && dentists.length === 0) {
        throw new Error(OTP_ERRORS.NO_CLINIC_OR_DENTIST_FOUND);
      }

      setCurrentStep(SIGN_IN_STEPS.VERIFICATION);
      setLoadingVerification(true);
      const { error } = await supabase.functions.invoke('otp', {
        body: {
          operation: 'send-with-owners',
          payload: {
            phone_number: `63${mobileNumber}`
          }
        }
      });

      if (error) {
        throw new Error(`Error sending OTP: ${error.message}`);
      }
    },
    onSuccess: () => {
      setIsResendDisabled(true);
    },
    onError: (error: Error) => {
      if (
        error.message === OTP_ERRORS.MOBILE_NUMBER_IS_REQUIRED ||
        error.message === OTP_ERRORS.NO_CLINIC_OR_DENTIST_FOUND
      ) {
        setIsResendDisabled(true);
        displayToast({
          message: error.message,
          duration: 5000,
          position: 'bottom',
          positionAnchor: 'next-button'
        });
        return;
      }

      setIsResendDisabled(false);
      displayToast({
        message: OTP_ERRORS.FAILED_TO_SEND_OTP,
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
    }
  });

  const resendOtpMutation = useMutation({
    mutationFn: async () => {
      if (!mobileNumber) throw new Error(OTP_ERRORS.MOBILE_NUMBER_IS_REQUIRED);

      const { error } = await supabase.functions.invoke('otp', {
        body: {
          operation: 'resend-with-owners',
          payload: {
            phone_number: `63${mobileNumber}`
          }
        }
      });

      if (error) {
        throw new Error(`Error sending OTP: ${error.message}`);
      }
    },
    onSuccess: () => {
      setIsResendDisabled(true);
    },
    onError: (error: Error) => {
      if (error.message === OTP_ERRORS.MOBILE_NUMBER_IS_REQUIRED) {
        setIsResendDisabled(true);
        displayToast({
          message: error.message,
          duration: 5000,
          position: 'bottom',
          positionAnchor: 'next-button'
        });
        return;
      }

      setIsResendDisabled(false);
      displayToast({
        message: OTP_ERRORS.FAILED_TO_RESEND_OTP,
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
    }
  });

  const verifyOtpMutation = useMutation({
    mutationFn: async (otp: string) => {
      if (!mobileNumber) throw new Error(OTP_ERRORS.MOBILE_NUMBER_IS_REQUIRED);

      const {
        data: { user },
        error
      } = await supabase.auth.verifyOtp({
        phone: `63${mobileNumber}`,
        token: otp,
        type: 'sms'
      });

      if (error) {
        throw new Error(`Error verifying OTP: ${error.message}`);
      }

      if (!user) {
        throw new Error(OTP_ERRORS.NO_CLINIC_OR_DENTIST_FOUND);
      }

      const dentistsByMobileNumber = await queryClient.fetchQuery({
        queryKey: ['dentistsByMobileNumber', mobileNumber],
        queryFn: () => getDentistsByMobileNumber(mobileNumber)
      });
      if (dentistsByMobileNumber.length > 0) {
        const userRecord = await signIn(false);

        if (userRecord) {
          const updatePromises = dentistsByMobileNumber.map((dentist) =>
            dentist.id
              ? updateDentist({
                  dentistId: dentist.id,
                  dentist: { ...dentist, user_id: userRecord.id }
                })
              : null
          );

          await Promise.all(updatePromises);
        }

        return { type: 'dentist', user };
      }

      const clinic = await queryClient.fetchQuery({
        queryKey: ['clinic', user.id],
        queryFn: () => getClinic(user.id)
      });
      if (!clinic) {
        throw new Error(OTP_ERRORS.NO_CLINIC_OR_DENTIST_FOUND);
      }

      await signIn(false);
      return { type: 'clinic', user };
    },
    onSuccess: async () => {
      await onDone();

      if (onAfterSignIn) {
        onAfterSignIn();
      }
    },
    onError: (error: Error) => {
      displayToast({
        message: error.message,
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
    }
  });

  const sendOtp = useCallback(async () => {
    if (mobileNumber && authenticated && `63${mobileNumber}` === user?.phone) {
      displayToast({
        message: 'Skipped verification.',
        duration: 2400,
        position: 'bottom',
        positionAnchor: 'next-button'
      });

      await signIn(false);
      await onDone();
      return;
    }

    await sendOtpMutation.mutateAsync();
  }, [mobileNumber, authenticated, user, signIn, onDone, sendOtpMutation]);

  const resendOtp = useCallback(async () => {
    await resendOtpMutation.mutateAsync();
  }, [resendOtpMutation]);

  const verifyOtp = useCallback(
    async (otp: string) => {
      await verifyOtpMutation.mutateAsync(otp).catch(() => {});
    },
    [verifyOtpMutation]
  );

  // Show a brief loading animation for verification page
  useEffect(() => {
    if (loadingVerification) {
      setTimeout(() => {
        setLoadingVerification(false);
      }, OTP_LOADING_DELAY_IN_MS);
    }
  }, [loadingVerification]);

  useEffect(() => {
    return () => {
      setCurrentStep(SIGN_IN_STEPS.MOBILE_NUMBER);
      setLoadingVerification(false);
      setMobileNumber(null);
    };
  }, []);

  const loading =
    loadingVerification ||
    resendOtpMutation.isPending ||
    verifyOtpMutation.isPending;

  return (
    <SignInContext.Provider
      value={{
        loading,
        currentStep,
        setCurrentStep,
        goBack,
        mobileNumber,
        setMobileNumber,
        sendOtp,
        resendOtp,
        verifyOtp,
        isResendDisabled,
        setIsResendDisabled
      }}
    >
      {children}
    </SignInContext.Provider>
  );
};

export const useSignIn = () => {
  const context = useContext(SignInContext);
  if (context === undefined) {
    throw new Error('useSignIn must be used within a SignInProvider');
  }
  return context;
};
